import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Gamepad2, TrendingUp, Clock, Award, Zap, Target } from 'lucide-react';
import { Helmet } from 'react-helmet';

const ISEInteractiveGuideBlog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>ISE Interactive Quick Study Guide 2025 | Master Institutional Securities Exam</title>
        <meta name="description" content="Master the Institutional Securities Exam (ISE) with our interactive study guide. 7 elements, institutional scenarios, corporate finance games, and full mock exam. Pass CIRO ISE on the first try." />
        <meta name="keywords" content="ISE exam, Institutional Securities Exam, CIRO ISE prep, institutional securities Canada, corporate investment exam, interactive ISE study guide, CIRO institutional course, investment banking exam Canada" />
        <meta property="og:title" content="ISE Interactive Quick Study Guide | Master the Institutional Securities Exam" />
        <meta property="og:description" content="Master all 7 institutional elements with interactive case studies and corporate finance scenarios. $49.99 CAD (Regular: $129.99) one-time payment." />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://vittafinance.ca/blog/ise-interactive-study-guide" />
      </Helmet>

      <article style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          padding: '80px 20px 60px',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 20px',
              borderRadius: '100px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              ISE EXAM PREPARATION 2025
            </div>
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: 900,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Master the ISE Exam with Canada's Interactive Study Guide
            </h1>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 32px',
              lineHeight: 1.6
            }}>
              Pass your Institutional Securities Examination on the first try with 7 interactive elements, corporate finance scenarios, and a comprehensive mock exam
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link
                to="/books/ise-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
              >
                <BookOpen size={22} />
                Get Instant Access - $49.99 CAD (Regular: $129.99)
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '60px 20px'
        }}>
          {/* Introduction */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '20px'
            }}>
              What is the ISE Exam?
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              The <strong>Institutional Securities Exam (ISE)</strong> is a mandatory licensing exam for professionals seeking to advise institutional clients in Canada's investment industry. Administered by the <strong>Canadian Investment Regulatory Organization (CIRO)</strong>, this specialized exam tests your knowledge of institutional securities, large block trading, corporate finance, and regulatory compliance for institutional business.
            </p>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              Whether you're pursuing a career as an <strong>Institutional Trader</strong>, <strong>Corporate Investment Advisor</strong>, or entering the institutional securities business in Ontario, British Columbia, Alberta, Quebec, or anywhere across Canada, the ISE is your gateway to serving corporate clients, pension funds, and institutional investors.
            </p>
          </section>

          {/* Why This Guide? */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Why Choose the ISE Interactive Quick Study Guide?
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px',
              marginBottom: '32px'
            }}>
              {[
                { icon: <Gamepad2 size={32} />, color: '#3b82f6', title: 'Case-Based Learning', desc: 'Real-world institutional scenarios and corporate finance simulations' },
                { icon: <Target size={32} />, color: '#10b981', title: 'Full-Length Mock Exam', desc: 'Complete practice exam with institutional focus' },
                { icon: <Clock size={32} />, color: '#f59e0b', title: '7 Focused Elements', desc: 'Comprehensive coverage of all CIRO ISE exam topics' },
                { icon: <Award size={32} />, color: '#8b5cf6', title: 'Exam Readiness Tracker', desc: 'Monitor your progress and identify weak areas' },
              ].map((feature, idx) => (
                <div key={idx} style={{
                  padding: '28px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ color: feature.color, marginBottom: '16px' }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#1e293b',
                    marginBottom: '8px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    lineHeight: 1.6,
                    color: '#64748b'
                  }}>
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>

            <div style={{
              background: '#f0f9ff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              padding: '28px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#1e40af',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Zap size={24} />
                Perfect for Institutional Investment Professionals
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.7,
                color: '#334155',
                marginBottom: '12px'
              }}>
                This guide is specifically designed for candidates across <strong>Canada</strong> preparing for the ISE exam in 2025. Master institutional securities, large transactions, and corporate client relationships with interactive learning.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'One-time payment of $49.99 CAD (Regular: $129.99)',
                  'Lifetime access to all updates',
                  'Mobile-friendly for studying anywhere',
                  'No recurring fees or hidden costs'
                ].map((point, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 0',
                    fontSize: '15px',
                    color: '#334155'
                  }}>
                    <CheckCircle size={20} color="#10b981" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* All 7 Elements Breakdown */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Complete Coverage: All 7 ISE Elements
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '32px'
            }}>
              Each element includes <strong>interactive case studies</strong>, <strong>corporate scenarios</strong>, and <strong>exam-style questions</strong>:
            </p>

            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              {[
                { num: 1, icon: '📘', title: 'Canadian Institutional Market Structure', desc: 'CIRO institutional rules, market participants, trading venues, block trading, institutional client types, and regulatory framework' },
                { num: 2, icon: '📗', title: 'Institutional Products & Securities', desc: 'Corporate debt, government securities, money market instruments, structured products, and institutional-grade securities' },
                { num: 3, icon: '📙', title: 'Corporate Finance Fundamentals', desc: 'Capital structure, financing decisions, IPOs, secondary offerings, private placements, and corporate actions' },
                { num: 4, icon: '📕', title: 'Institutional Trading & Execution', desc: 'Block trade execution, algorithmic trading, VWAP/TWAP strategies, dark pools, and trade reporting requirements' },
                { num: 5, icon: '📓', title: 'Institutional Client Relationships', desc: 'Know Your Client for institutions, pension fund regulations, corporate treasuries, suitability for institutional accounts' },
                { num: 6, icon: '📔', title: 'Risk & Portfolio Management', desc: 'Institutional risk frameworks, VaR, stress testing, portfolio construction for institutions, and performance measurement' },
                { num: 7, icon: '🎓', title: 'Ethics & Institutional Conduct', desc: 'CIRO ethical standards for institutional business, conflicts of interest, information barriers, and market abuse prevention' },
              ].map((element) => (
                <div key={element.num} style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderLeft: '4px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.2s'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px'
                  }}>
                    <span style={{ fontSize: '40px' }}>{element.icon}</span>
                    <div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#1e293b',
                        marginBottom: '8px'
                      }}>
                        Element {element.num}: {element.title}
                      </h3>
                      <p style={{
                        fontSize: '15px',
                        lineHeight: 1.7,
                        color: '#64748b'
                      }}>
                        {element.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Mock Exam Feature */}
          <section style={{ marginBottom: '60px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              padding: '40px',
              border: '2px solid #f59e0b'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '48px' }}>🎯</span>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: 800,
                  color: '#78350f',
                  margin: 0
                }}>
                  Full-Length ISE Mock Exam
                </h2>
              </div>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.7,
                color: '#78350f',
                marginBottom: '20px'
              }}>
                Test your exam readiness with a complete institutional securities mock exam featuring:
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {[
                  'Exam Readiness Gauge',
                  'Institutional Scenarios',
                  'Corporate Finance Cases',
                  'Trading Mechanics Questions',
                  'Time Management Analytics',
                  'Detailed Explanations'
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#78350f',
                    fontWeight: 600
                  }}>
                    <CheckCircle size={18} color="#f59e0b" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Path */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Your Career in Institutional Securities
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '20px'
            }}>
              Passing the ISE exam opens doors to prestigious institutional finance careers. As a licensed institutional securities professional, you'll:
            </p>
            <ul style={{
              fontSize: '17px',
              lineHeight: 2,
              color: '#475569',
              paddingLeft: '24px'
            }}>
              <li><strong>Work with Major Institutions:</strong> Serve pension funds, corporations, insurance companies, and institutional investors</li>
              <li><strong>Execute Large Transactions:</strong> Handle multi-million dollar block trades and strategic placements</li>
              <li><strong>Higher Earning Potential:</strong> Institutional roles typically offer higher base salaries plus performance bonuses</li>
              <li><strong>Corporate Finance Exposure:</strong> Participate in IPOs, debt offerings, and major corporate finance transactions</li>
              <li><strong>Elite Professional Network:</strong> Build relationships with senior corporate executives and institutional decision-makers</li>
            </ul>
          </section>

          {/* Canadian Context */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Built for Canadian Institutional Securities
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              This guide covers all Canadian-specific institutional regulations:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {[
                'CIRO institutional trading rules',
                'Canadian block trade regulations',
                'TSX institutional market structure',
                'Corporate finance Canadian framework',
                'Pension fund regulations (CAPSA)',
                'Provincial institutional requirements',
                'Institutional KYC obligations',
                'Market integrity for large trades'
              ].map((item, idx) => (
                <div key={idx} style={{
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#334155',
                  fontWeight: 600
                }}>
                  ✓ {item}
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: '20px',
            padding: '48px 40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: 900,
              marginBottom: '16px'
            }}>
              Start Your ISE Exam Prep Today
            </h2>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px'
            }}>
              Join Canadian institutional professionals who've used interactive learning to pass their ISE exam and advance their careers
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center'
            }}>
              <Link
                to="/books/ise-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '18px 40px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '20px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                <BookOpen size={24} />
                Get Instant Access - $49.99 CAD (Regular: $129.99)
              </Link>
              <p style={{
                fontSize: '15px',
                opacity: 0.9
              }}>
                One-time payment • Lifetime access • All updates included
              </p>
            </div>
          </section>
          {/* FAQ Section */}
          <section style={{ marginTop: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Frequently Asked Questions
            </h2>
            
            {[
              {
                q: 'What\'s the difference between ISE and RSE?',
                a: 'ISE focuses on institutional clients (corporations, pension funds) and large block trades, while RSE focuses on retail clients (individual investors). ISE professionals typically work in institutional sales, trading desks, or corporate finance.'
              },
              {
                q: 'How long do I have access?',
                a: 'Lifetime access! Pay once ($49.99 CAD (Regular: $129.99)) and access all 7 elements, case studies, games, and the mock exam forever. All future updates are included at no extra cost.'
              },
              {
                q: 'Do I need prior experience to take the ISE?',
                a: 'While CIRO doesn\'t require prior institutional experience to write the exam, most candidates have some industry exposure. The interactive guide teaches concepts from the ground up, making it accessible to determined learners.'
              },
              {
                q: 'Can I use this in Quebec?',
                a: 'Yes! The guide covers all Canadian provinces including Quebec-specific institutional requirements and AMF regulations.'
              },
              {
                q: 'Is this suitable for career changers?',
                a: 'If you already hold CIRE or RSE and want to transition to institutional business, this guide provides everything you need. The interactive format helps career changers quickly grasp institutional concepts.'
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                marginBottom: '24px',
                padding: '24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  {faq.q}
                </h3>
                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  margin: 0
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </section>


          {/* Related Resources Section */}
          <section style={{ marginTop: '60px', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              More ISE Exam Preparation Resources
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {/* ISE Course */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  ONLINE COURSE
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  ISE Complete Course
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  Comprehensive prep for institutional securities with case studies, practice exams, and expert instruction.
                </p>
                <a
                  href="https://vittafinance.mykajabi.com/offers/W6ezafud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#1e3a8a',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  View Course - $29.99/mo
                </a>
              </div>

              {/* Free Study Guide */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #10b981',
                boxShadow: '0 4px 12px rgba(16,185,129,0.15)'
              }}>
                <div style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  FREE RESOURCE
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  Free ISE Study Guide
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  11-phase structured learning path covering institutional securities. 8-10 weeks preparation plan.
                </p>
                <Link
                  to="/ise-study-guide"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#10b981',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  Get Free Study Guide
                </Link>
              </div>

              {/* Diagnostic Tool */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #f59e0b',
                boxShadow: '0 4px 12px rgba(245,158,11,0.15)'
              }}>
                <div style={{
                  background: '#f59e0b',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  FREE TOOL
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  Exam Diagnostic Tool
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  Assess your readiness with our diagnostic tool. Get personalized study recommendations.
                </p>
                <Link
                  to="/diagnostic-tool"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#f59e0b',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  Try Diagnostic Tool
                </Link>
              </div>
            </div>

            {/* Other Interactive Guides */}
            <div style={{
              background: '#f0f9ff',
              borderRadius: '16px',
              padding: '32px',
              border: '2px solid #3b82f6'
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#1e40af',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                Explore Other Interactive Study Guides
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginTop: '24px'
              }}>
                <Link
                  to="/books/cire-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📘</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>CIRE Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$34.99 CAD</div>
                </Link>
                <Link
                  to="/books/rse-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📗</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>RSE Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$49.99 CAD</div>
                </Link>
                <Link
                  to="/books/derivatives-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Derivatives Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$49.99 CAD</div>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </article>
    </>
  );
};

export default ISEInteractiveGuideBlog;